import { useState } from 'react';
import { Link } from 'react-router-dom';
import { celebrityDreamMap } from '../../../mocks/celebrityDreams';

interface Props {
  slug: string;
  name: string;
  dayMaster: string;
}

const elementColors: Record<string, { text: string; bg: string; border: string }> = {
  '금(金)': { text: 'text-slate-300', bg: 'bg-slate-500/10', border: 'border-slate-500/30' },
  '화(火)': { text: 'text-red-400', bg: 'bg-red-500/10', border: 'border-red-500/30' },
  '수(水)': { text: 'text-sky-400', bg: 'bg-sky-500/10', border: 'border-sky-500/30' },
  '목(木)': { text: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500/30' },
  '토(土)': { text: 'text-amber-500', bg: 'bg-amber-500/10', border: 'border-amber-500/30' },
};

export default function CelebrityDreamSection({ slug, name, dayMaster }: Props) {
  const [activeDream, setActiveDream] = useState(0);
  const data = celebrityDreamMap[slug];

  if (!data) return null;

  const elemStyle = elementColors[data.elementDream] ?? elementColors['금(金)'];

  return (
    <section className="p-6 rounded-2xl bg-gradient-to-br from-indigo-900/20 via-[#0f0a1e]/90 to-purple-900/20 border border-indigo-500/20">
      {/* Header */}
      <h2
        className="text-white text-xl font-bold mb-2 flex items-center gap-3"
        style={{ fontFamily: '"Noto Serif KR", serif' }}
      >
        <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-indigo-500/20 border border-indigo-500/30 text-indigo-300 text-sm font-bold">夢</span>
        {name}의 사주로 보는 꿈해몽
      </h2>
      <p className="text-purple-300/60 text-sm mb-5 pl-11">
        {dayMaster} 기준 — 이 사주를 가진 사람에게 특히 의미 있는 꿈 상징
      </p>

      {/* Intro */}
      <div className="flex items-start gap-3 p-4 rounded-xl bg-white/3 border border-white/8 mb-5">
        <div className="w-8 h-8 flex items-center justify-center flex-shrink-0 mt-0.5">
          <i className="ri-moon-fill text-indigo-300 text-lg"></i>
        </div>
        <p className="text-purple-200 text-sm leading-relaxed">{data.intro}</p>
      </div>

      {/* Element badge */}
      <div className="flex items-center gap-2 mb-4">
        <span className="text-purple-400/60 text-xs">핵심 꿈 오행:</span>
        <span className={`px-2.5 py-0.5 rounded-full border text-xs font-bold ${elemStyle.text} ${elemStyle.bg} ${elemStyle.border}`}>
          {data.elementDream}
        </span>
      </div>

      {/* Dream cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        {data.dreams.map((dream, i) => (
          <button
            key={i}
            onClick={() => setActiveDream(i)}
            className={`p-3 rounded-xl border text-left transition-all duration-200 cursor-pointer group ${
              activeDream === i
                ? 'border-indigo-500/60 bg-indigo-500/15'
                : 'border-white/8 bg-white/3 hover:border-indigo-500/30 hover:bg-white/5'
            }`}
          >
            <span className="text-2xl block mb-2 group-hover:scale-110 transition-transform duration-200">{dream.emoji}</span>
            <p className={`text-xs font-bold mb-0.5 ${activeDream === i ? 'text-indigo-200' : 'text-white'}`}>
              {dream.symbol}
            </p>
            <p className="text-purple-400/60 text-[10px] leading-tight">{dream.meaning}</p>
          </button>
        ))}
      </div>

      {/* Active dream detail */}
      {data.dreams[activeDream] && (
        <div className="p-4 rounded-xl bg-indigo-900/30 border border-indigo-500/20 mb-5 animate-fadeIn">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xl">{data.dreams[activeDream].emoji}</span>
            <p className="text-indigo-200 font-bold text-sm">{data.dreams[activeDream].symbol} 꿈의 의미</p>
          </div>
          <p className="text-purple-200/80 text-sm leading-relaxed">{data.dreams[activeDream].significance}</p>
        </div>
      )}

      {/* Message */}
      <div className="p-4 rounded-xl bg-gradient-to-br from-primary-gold/5 to-amber-900/10 border border-primary-gold/20 mb-5">
        <p className="text-primary-gold text-xs font-semibold mb-1.5 flex items-center gap-1">
          <div className="w-4 h-4 flex items-center justify-center">
            <i className="ri-sparkling-2-fill text-xs"></i>
          </div>
          종합 꿈해몽 메시지
        </p>
        <p className="text-purple-100 text-sm leading-relaxed">{data.message}</p>
      </div>

      {/* CTA to dream page */}
      <Link
        to="/dream"
        className="flex items-center justify-center gap-2 w-full py-3 rounded-xl bg-gradient-to-r from-indigo-600/40 to-purple-600/40 border border-indigo-500/30 text-indigo-200 text-sm font-semibold hover:border-indigo-400/60 hover:text-white transition-all duration-300 cursor-pointer whitespace-nowrap"
      >
        <div className="w-5 h-5 flex items-center justify-center">
          <i className="ri-moon-fill text-indigo-300 text-base"></i>
        </div>
        <span>내 꿈도 AI로 해몽받기</span>
        <div className="w-4 h-4 flex items-center justify-center">
          <i className="ri-arrow-right-line text-sm"></i>
        </div>
      </Link>
    </section>
  );
}
